import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, MagnifyingGlass, Funnel, Export, Eye, PencilSimple, Trash, Buildings, Globe, MapPin, FileXls, UploadSimple, DownloadSimple, FilePdf, ShieldCheck, Handshake as HandshakeIcon, FileText as FileTextIcon, PauseCircle as PauseCircleIcon, XCircle as XCircleIcon, Archive as ArchiveIcon } from '@phosphor-icons/react';
import { getOrganizations, createOrganization, updateOrganization, deleteOrganization, lookupSirene, importOrganizationsExcel, downloadOrganizationsTemplate, exportOrganizations, getOrganizationMissions } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { PageHero } from '../components/motion/PageHero';
import ClientInvoicesPanel from '../components/ClientInvoicesPanel';
import { DataTable } from '../components/DataTable';
import { PhoneInput } from '../components/PhoneInput';
import { Flag } from '../components/Flag';
import { CurrencyInput } from '../components/CurrencyInput';
import { NafAutocomplete } from '../components/NafAutocomplete';
import { TERRITOIRES, findTerritoire } from '../constants/countries';

const formeJuridiqueOptions = [
  { value: 'SARL', label: 'SARL' },
  { value: 'SAS', label: 'SAS' },
  { value: 'SA', label: 'SA' },
  { value: 'SCOP', label: 'SCOP' },
  { value: 'SCIC', label: 'SCIC' },
  { value: 'Association', label: 'Association' },
  { value: 'Collectivité', label: 'Collectivité' },
  { value: 'EI', label: 'Entreprise Individuelle' },
  { value: 'Auto-entrepreneur', label: 'Auto-entrepreneur' },
];

const statutOptions = [
  { value: 'prospect',         label: 'Prospect',         Icon: MagnifyingGlass, iconColor: '#2563EB' },
  { value: 'en_qualification', label: 'En qualification', Icon: Funnel,          iconColor: '#B45309' },
  { value: 'membre_actif',     label: 'Membre actif',     Icon: ShieldCheck,     iconColor: '#059669' },
  { value: 'partenaire_actif', label: 'Partenaire actif', Icon: HandshakeIcon,   iconColor: '#16A34A' },
  { value: 'sous_convention',  label: 'Sous convention',  Icon: FileTextIcon,    iconColor: '#2D5A4C' },
  { value: 'suspendu',         label: 'Suspendu',         Icon: PauseCircleIcon, iconColor: '#EA580C' },
  { value: 'cloture',          label: 'Clôturé',          Icon: XCircleIcon,     iconColor: '#DC2626' },
  { value: 'archive',          label: 'Archivé',          Icon: ArchiveIcon,     iconColor: '#475569' },
];
const findStatut = (v) => statutOptions.find(s => s.value === v);

const territoireOptions = TERRITOIRES;

const getStatutColor = (statut) => {
  const colors = {
    prospect: 'bg-blue-100 text-blue-800',
    en_qualification: 'bg-yellow-100 text-yellow-800',
    membre_actif: 'bg-green-100 text-green-800',
    partenaire_actif: 'bg-emerald-100 text-emerald-800',
    sous_convention: 'bg-purple-100 text-purple-800',
    suspendu: 'bg-orange-100 text-orange-800',
    cloture: 'bg-gray-100 text-gray-800',
    archive: 'bg-slate-100 text-slate-800',
  };
  return colors[statut] || 'bg-gray-100 text-gray-800';
};

export function OrganizationsPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTerritoire, setFilterTerritoire] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterMembreFtpe, setFilterMembreFtpe] = useState('');
  const [filterFormeJuridique, setFilterFormeJuridique] = useState('');
  const [filterHasSiren, setFilterHasSiren] = useState('');
  const [filterHasSiret, setFilterHasSiret] = useState('');
  const [filterCaMin, setFilterCaMin] = useState('');
  const [filterCaMax, setFilterCaMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgMissions, setOrgMissions] = useState([]);
  const [formData, setFormData] = useState({
    raison_sociale: '',
    enseigne: '',
    forme_juridique: '',
    siren: '',
    siret: '',
    adresse_siege: '',
    adresse_batiment: '',
    adresse_porte: '',
    adresse_quartier: '',
    ville: '',
    code_postal: '',
    territoire: '',
    activite_principale: '',
    code_naf: '',
    naf_label: '',
    effectif: '',
    chiffre_affaires: null,
    site_web: '',
    email: '',
    telephone: '',
    telephone_indicatif: '',
    membre_ftpe: false,
    statut: 'prospect',
  });
  const [sireneState, setSireneState] = useState({ loading: false, message: '', tone: 'idle', autoTriggered: '' });
  const sireneDebRef = React.useRef(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = React.useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadOrganizationsTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'import_organisations_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur lors du téléchargement du modèle'); }
  };
  const handleImportSubmit = async () => {
    const f = importFileRef.current?.files?.[0];
    if (!f) { toast.error('Sélectionnez un fichier Excel'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('skip_duplicates', 'true');
      const res = await importOrganizationsExcel(fd);
      setImportResult(res.data);
      toast.success(`Import terminé : ${res.data.inserted} créée(s), ${res.data.skipped} doublon(s)`);
      fetchOrganizations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'import');
    } finally { setImporting(false); }
  };

  const isValidSiretLuhn = (raw) => {
    const v = (raw || '').replace(/\D/g, '');
    if (v.length !== 14) return false;
    let sum = 0;
    for (let i = v.length - 1, j = 0; i >= 0; i--, j++) {
      let n = parseInt(v[i], 10);
      if (j % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
      sum += n;
    }
    return sum % 10 === 0;
  };

  const applySireneToForm = (data) => {
    setFormData((prev) => {
      const next = { ...prev };
      const setIfEmpty = (key, val) => { if (val && (next[key] === '' || next[key] == null)) next[key] = val; };
      setIfEmpty('raison_sociale', data.denomination);
      setIfEmpty('siret', data.siret);
      if (data.siret && data.siret.length === 14) setIfEmpty('siren', data.siret.slice(0, 9));
      setIfEmpty('code_naf', data.code_naf);
      setIfEmpty('naf_label', data.naf_label);
      setIfEmpty('activite_principale', data.naf_label);
      setIfEmpty('adresse_siege', data.adresse);
      setIfEmpty('code_postal', data.code_postal);
      setIfEmpty('ville', data.ville);
      return next;
    });
  };

  const triggerSireneLookup = async ({ silent = false } = {}) => {
    const raw = (formData.siret || '').replace(/\D/g, '');
    if (!isValidSiretLuhn(raw)) {
      if (!silent) setSireneState({ loading: false, message: 'SIRET invalide (14 chiffres + clé de Luhn)', tone: 'error', autoTriggered: '' });
      return;
    }
    setSireneState((s) => ({ ...s, loading: true, message: 'Recherche INSEE Sirene…', tone: 'loading' }));
    try {
      const res = await lookupSirene(raw);
      const data = res.data || {};
      applySireneToForm(data);
      const filled = [];
      if (data.denomination) filled.push('raison sociale');
      if (data.code_naf) filled.push('NAF');
      if (data.adresse || data.code_postal || data.ville) filled.push('adresse');
      const suffix = filled.length ? ` — ${filled.join(', ')} récupéré(s)` : '';
      const closedTag = data.ferme ? ' ⚠️ établissement fermé' : '';
      setSireneState({ loading: false, message: `${data.denomination || raw}${suffix}${closedTag}`, tone: data.ferme ? 'warning' : 'success', autoTriggered: raw });
      if (!silent) toast.success(`SIRENE : ${data.denomination || raw}${closedTag}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Recherche SIRENE échouée';
      setSireneState({ loading: false, message: msg, tone: 'error', autoTriggered: '' });
      if (!silent) toast.error(msg);
    }
  };

  // Auto declenchement quand SIRET valide (creation uniquement)
  useEffect(() => {
    if (editingOrg) return;
    const raw = (formData.siret || '').replace(/\D/g, '');
    if (!isValidSiretLuhn(raw)) return;
    if (sireneState.autoTriggered === raw) return;
    if (sireneDebRef.current) clearTimeout(sireneDebRef.current);
    sireneDebRef.current = setTimeout(() => { triggerSireneLookup({ silent: true }); }, 500);
    return () => sireneDebRef.current && clearTimeout(sireneDebRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.siret, editingOrg]);

  useEffect(() => {
    if (!dialogOpen) setSireneState({ loading: false, message: '', tone: 'idle', autoTriggered: '' });
  }, [dialogOpen]);

  const fetchOrganizations = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTerritoire && filterTerritoire !== 'all') params.territoire = filterTerritoire;
      if (filterStatut && filterStatut !== 'all') params.statut = filterStatut;
      if (filterFormeJuridique && filterFormeJuridique !== 'all') params.forme_juridique = filterFormeJuridique;
      if (filterMembreFtpe && filterMembreFtpe !== 'all') params.membre_ftpe = filterMembreFtpe === 'true';
      if (filterHasSiren === 'with') params.has_siren = true;
      if (filterHasSiren === 'without') params.has_siren = false;
      if (filterHasSiret === 'with') params.has_siret = true;
      if (filterHasSiret === 'without') params.has_siret = false;
      if (filterCaMin) params.ca_min = parseFloat(filterCaMin);
      if (filterCaMax) params.ca_max = parseFloat(filterCaMax);

      const res = await getOrganizations(params);
      setOrganizations(res.data.items || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des organisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [search, filterTerritoire, filterStatut, filterFormeJuridique, filterMembreFtpe, filterHasSiren, filterHasSiret, filterCaMin, filterCaMax]);

  const resetFilters = () => {
    setFilterTerritoire(''); setFilterStatut(''); setFilterFormeJuridique('');
    setFilterMembreFtpe(''); setFilterHasSiren(''); setFilterHasSiret('');
    setFilterCaMin(''); setFilterCaMax('');
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTerritoire && filterTerritoire !== 'all') params.territoire = filterTerritoire;
      if (filterStatut && filterStatut !== 'all') params.statut = filterStatut;
      if (filterFormeJuridique && filterFormeJuridique !== 'all') params.forme_juridique = filterFormeJuridique;
      if (filterMembreFtpe && filterMembreFtpe !== 'all') params.membre_ftpe = filterMembreFtpe === 'true';
      if (filterHasSiren === 'with') params.has_siren = true;
      if (filterHasSiren === 'without') params.has_siren = false;
      if (filterHasSiret === 'with') params.has_siret = true;
      if (filterHasSiret === 'without') params.has_siret = false;
      if (filterCaMin) params.ca_min = parseFloat(filterCaMin);
      if (filterCaMax) params.ca_max = parseFloat(filterCaMax);
      const res = await exportOrganizations(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organisations_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        effectif: formData.effectif ? parseInt(formData.effectif) : null,
        chiffre_affaires: (formData.chiffre_affaires === '' || formData.chiffre_affaires == null) ? null : Number(formData.chiffre_affaires),
      };
      
      if (editingOrg) {
        await updateOrganization(editingOrg.id, data);
        toast.success('Organisation mise à jour');
      } else {
        await createOrganization(data);
        toast.success('Organisation créée');
      }
      setDialogOpen(false);
      setEditingOrg(null);
      resetForm();
      fetchOrganizations();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur'));
    }
  };

  const resetForm = () => {
    setOrgMissions([]);
    setFormData({
      raison_sociale: '',
      enseigne: '',
      forme_juridique: '',
      siren: '',
      siret: '',
      adresse_siege: '',
      adresse_batiment: '',
      adresse_porte: '',
      adresse_quartier: '',
      ville: '',
      code_postal: '',
      territoire: '',
      activite_principale: '',
      code_naf: '',
      naf_label: '',
      effectif: '',
      chiffre_affaires: null,
      site_web: '',
      email: '',
      telephone: '',
      telephone_indicatif: '',
      membre_ftpe: false,
      statut: 'prospect',
    });
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      raison_sociale: org.raison_sociale || '',
      enseigne: org.enseigne || '',
      forme_juridique: org.forme_juridique || '',
      siren: org.siren || '',
      siret: org.siret || '',
      adresse_siege: org.adresse_siege || '',
      adresse_batiment: org.adresse_batiment || '',
      adresse_porte: org.adresse_porte || '',
      adresse_quartier: org.adresse_quartier || '',
      ville: org.ville || '',
      code_postal: org.code_postal || '',
      territoire: org.territoire || '',
      activite_principale: org.activite_principale || '',
      code_naf: org.code_naf || '',
      naf_label: org.naf_label || '',
      effectif: org.effectif?.toString() || '',
      chiffre_affaires: org.chiffre_affaires ?? null,
      site_web: org.site_web || '',
      email: org.email || '',
      telephone: org.telephone || '',
      telephone_indicatif: org.telephone_indicatif || '',
      membre_ftpe: org.membre_ftpe || false,
      statut: org.statut || 'prospect',
    });
    setDialogOpen(true);
    // Charger les missions coopératives portées par cette organisation
    getOrganizationMissions(org.id)
      .then((r) => setOrgMissions(r.data.items || []))
      .catch(() => setOrgMissions([]));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) {
      try {
        await deleteOrganization(id);
        toast.success('Organisation supprimée');
        fetchOrganizations();
      } catch (error) {
        toast.error(formatApiError(error, 'Erreur lors de la suppression'));
      }
    }
  };

  const openNewDialog = () => {
    setEditingOrg(null);
    resetForm();
    setDialogOpen(true);
  };

  // Keyboard shortcut: 'n' opens new org dialog
  useEffect(() => {
    const handleNew = () => { setEditingOrg(null); resetForm(); setDialogOpen(true); };
    window.addEventListener('crm-shortcut-new', handleNew);
    return () => window.removeEventListener('crm-shortcut-new', handleNew);
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Organisations"
        subtitle="Entreprises, associations et structures partenaires de l'écosystème ESS"
        icon={<Buildings size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#00AEEF', '#FF8E00']}
        actions={(
          <>
            <Button onClick={() => setImportDialog(true)} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="import-org-btn">
              <FileXls size={18} className="mr-2" />
              Importer
            </Button>
            <Button onClick={openNewDialog} className="bg-[#FF8E00] text-white hover:bg-[#e07d00]" data-testid="new-org-btn">
              <Plus size={18} className="mr-2" />
              Nouvelle
            </Button>
          </>
        )}
      />

      {/* Search and Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                type="search"
                placeholder="Rechercher par nom, SIREN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E8E6E1]"
                data-testid="org-search"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-[#E8E6E1]">
              <Funnel size={18} className="mr-2" />
              Filtres
            </Button>
            <Button variant="outline" className="border-[#E8E6E1]" onClick={handleExport} data-testid="export-orgs-btn">
              <Export size={18} className="mr-2" />
              Exporter
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E8E6E1]">
              <Select value={filterTerritoire} onValueChange={setFilterTerritoire}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                  <SelectValue placeholder="Territoire" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {territoireOptions.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">
                        <Flag iso={t.iso} size={16} />
                        <span>{t.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {statutOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterMembreFtpe} onValueChange={setFilterMembreFtpe}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]" data-testid="filter-org-ftpe">
                  <SelectValue placeholder="Membre FTPE" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="true">Membres FTPE</SelectItem>
                  <SelectItem value="false">Non membres</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterFormeJuridique} onValueChange={setFilterFormeJuridique}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]" data-testid="filter-org-forme">
                  <SelectValue placeholder="Statut juridique" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {formeJuridiqueOptions.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterHasSiren} onValueChange={setFilterHasSiren}>
                <SelectTrigger className="w-[160px] border-[#E8E6E1]" data-testid="filter-org-siren">
                  <SelectValue placeholder="SIREN" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">SIREN : tous</SelectItem>
                  <SelectItem value="with">Avec SIREN</SelectItem>
                  <SelectItem value="without">Sans SIREN</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterHasSiret} onValueChange={setFilterHasSiret}>
                <SelectTrigger className="w-[160px] border-[#E8E6E1]" data-testid="filter-org-siret">
                  <SelectValue placeholder="SIRET" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">SIRET : tous</SelectItem>
                  <SelectItem value="with">Avec SIRET</SelectItem>
                  <SelectItem value="without">Sans SIRET</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-1">
                <Input type="number" placeholder="CA min €" value={filterCaMin} onChange={(e) => setFilterCaMin(e.target.value)} className="w-[130px] border-[#E8E6E1]" data-testid="filter-org-ca-min" />
                <span className="text-[#5E6D66]">–</span>
                <Input type="number" placeholder="CA max €" value={filterCaMax} onChange={(e) => setFilterCaMax(e.target.value)} className="w-[130px] border-[#E8E6E1]" data-testid="filter-org-ca-max" />
              </div>

              <Button variant="ghost" onClick={resetFilters} data-testid="filter-org-reset" className="text-[#5E6D66]">Réinitialiser</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : organizations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5E6D66]">Aucune organisation trouvee</p>
              <Button onClick={openNewDialog} variant="link" className="text-[#2D5A4C] mt-2">
                Creer votre premiere organisation
              </Button>
            </div>
          ) : (
            <DataTable
              data={organizations}
              columns={[
                {
                  key: 'raison_sociale',
                  label: 'Organisation',
                  render: (val, row) => (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#8DB0A1]/20 rounded-lg flex items-center justify-center shrink-0">
                        <Buildings size={20} className="text-[#2D5A4C]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1A2421]">{val}</p>
                        {row.enseigne && <p className="text-sm text-[#5E6D66]">{row.enseigne}</p>}
                        {row.email && <p className="text-xs text-[#00AEEF]">{row.email}</p>}
                        {row.forme_juridique && <Badge variant="outline" className="text-xs mt-1">{row.forme_juridique}</Badge>}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'siren',
                  label: 'SIREN/SIRET',
                  render: (val, row) => (
                    <div className="text-sm">
                      {val && <p className="text-[#1A2421]">SIREN: {val}</p>}
                      {row.siret && <p className="text-[#5E6D66]">SIRET: {row.siret}</p>}
                    </div>
                  ),
                },
                {
                  key: 'territoire',
                  label: 'Territoire',
                  render: (val, row) => (
                    <div>
                      {val && <div className="flex items-center gap-1 text-sm text-[#5E6D66]"><MapPin size={14} />{val}</div>}
                      {row.ville && <p className="text-xs text-[#5E6D66]">{row.ville}</p>}
                    </div>
                  ),
                },
                {
                  key: 'chiffre_affaires',
                  label: "Chiffre d'affaires",
                  render: (val) => val != null && val !== '' ? (
                    <span className="text-sm text-[#1A2421] tabular-nums">
                      {Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  ) : <span className="text-xs text-[#5E6D66]">—</span>,
                },
                {
                  key: 'membre_ftpe',
                  label: 'FTPE',
                  render: (val) => val
                    ? <Badge className="bg-[#2D5A4C] text-white">Membre</Badge>
                    : <Badge variant="outline" className="text-[#5E6D66]">Non</Badge>,
                },
                {
                  key: 'date_creation',
                  label: 'Créée le',
                  render: (val) => {
                    if (!val) return <span className="text-xs text-[#5E6D66]">—</span>;
                    const d = new Date(val);
                    if (isNaN(d.getTime())) return <span className="text-xs text-[#5E6D66]">{val}</span>;
                    return <span className="text-xs text-[#5E6D66] tabular-nums">{d.toLocaleDateString('fr-FR')}</span>;
                  },
                },
                {
                  key: 'statut',
                  label: 'Statut',
                  render: (val) => {
                    const s = findStatut(val);
                    const I = s?.Icon;
                    return (
                      <Badge className={`${getStatutColor(val)} gap-1.5`}>
                        {I && <I size={12} weight="fill" style={{ color: s.iconColor }} />}
                        {s?.label || val}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  className: 'text-right',
                  cellClassName: 'text-right',
                  render: (_, row) => (
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {row.ged_fiche_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${row.ged_fiche_id}/view`, '_blank')}
                          title="Voir la fiche PDF"
                          data-testid={`org-view-fiche-${row.id}`}
                        >
                          <FilePdf size={16} className="text-[#2D5A4C]" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/organizations/${row.id}`)}>
                        <Eye size={16} />
                      </Button>
                      {row.email && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={async () => {
                            if (!window.confirm(`Envoyer un kit d'adhésion à ${row.email} ?`)) return;
                            try {
                              const { sendAdhesionKit } = await import('../services/api');
                              await sendAdhesionKit({ organization_id: row.id, kit_type: 'associe_pm' });
                              toast.success('Kit PDF transmis au service email');
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi'); }
                          }}
                          title="Envoyer kit d'adhésion PM"
                          data-testid={`send-kit-org-${row.id}`}
                        >
                          <HandshakeIcon size={16} className="text-[#2D5A4C]" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-[#D47A62]">
                        <Trash size={16} />
                      </Button>
                    </div>
                  ),
                },
              ]}
              searchKeys={['raison_sociale', 'enseigne', 'siren', 'siret', 'email', 'ville']}
              showSearch={false}
              emptyMessage="Aucune organisation trouvee"
              data-testid="organizations-datatable"
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingOrg ? 'Modifier l\'organisation' : 'Nouvelle organisation'}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          {editingOrg && orgMissions.length > 0 && (
            <div className="p-3 bg-[#F0EFEB] rounded border border-[#E8E6E1] mb-3" data-testid="org-missions-section">
              <p className="text-xs font-semibold text-[#1A2421] mb-2 flex items-center gap-1">
                Missions coopératives portées <span className="text-[10px] text-[#5E6D66]">({orgMissions.length})</span>
              </p>
              <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                {orgMissions.map((m) => {
                  const isCipa = m.type_dossier === 'mission_coop_cipaesse';
                  return (
                    <div key={m.id} className="flex items-center justify-between text-xs bg-white p-2 rounded border border-[#E8E6E1]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${isCipa ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>{isCipa ? 'CIPAESSE' : 'CIPASCOP'}</span>
                          <span className="text-[10px] font-mono text-[#5E6D66]">{m.numero_dossier}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{m.statut}</span>
                        </div>
                        <p className="text-xs font-medium text-[#1A2421] truncate">{m.mission_intitule || m.objet_besoin || '—'}</p>
                        <div className="text-[10px] text-[#5E6D66]">{m.mission_lieu || '—'} · {m.mission_date_debut || '—'} → {m.mission_date_fin || '—'}</div>
                      </div>
                      {m.mission_paiement_previsionnel != null && (
                        <span className="text-xs font-bold text-[#2D5A4C] ml-2">{Number(m.mission_paiement_previsionnel).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {editingOrg && editingOrg.id && (
            <div className="mb-3" data-testid="org-invoices-wrap">
              <ClientInvoicesPanel organizationId={editingOrg.id} />
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Raison sociale *</Label>
                <Input
                  value={formData.raison_sociale}
                  onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                  required
                  data-testid="input-raison-sociale"
                />
              </div>
              <div className="space-y-2">
                <Label>Enseigne</Label>
                <Input
                  value={formData.enseigne}
                  onChange={(e) => setFormData({ ...formData, enseigne: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Statut juridique</Label>
                <Select value={formData.forme_juridique} onValueChange={(v) => setFormData({ ...formData, forme_juridique: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {formeJuridiqueOptions.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>SIREN</Label>
                <Input
                  value={formData.siren}
                  onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <Label>SIRET</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    maxLength={14}
                    placeholder="14 chiffres"
                    data-testid="input-org-siret"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={sireneState.loading || !isValidSiretLuhn(formData.siret)}
                    onClick={() => triggerSireneLookup({ silent: false })}
                    data-testid="btn-org-sirene-lookup"
                    className="whitespace-nowrap"
                    title="Rechercher dans la base SIRENE (INSEE)"
                  >
                    {sireneState.loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MagnifyingGlass size={16} weight="bold" />
                    )}
                  </Button>
                </div>
                {sireneState.message && (
                  <p
                    data-testid="org-sirene-status"
                    className={
                      'text-xs ' +
                      (sireneState.tone === 'error' ? 'text-red-600' :
                       sireneState.tone === 'warning' ? 'text-amber-700' :
                       sireneState.tone === 'success' ? 'text-emerald-700' :
                       'text-[#5E6D66]')
                    }
                  >
                    {sireneState.message}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-[#E8E6E1] pt-4">
              <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-3">Adresse du siège</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>N° et rue</Label>
                    <Input
                      value={formData.adresse_siege}
                      onChange={(e) => setFormData({ ...formData, adresse_siege: e.target.value })}
                      placeholder="12 rue des Flamboyants"
                      data-testid="input-org-adresse-rue"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quartier / Lieu-dit</Label>
                    <Input
                      value={formData.adresse_quartier}
                      onChange={(e) => setFormData({ ...formData, adresse_quartier: e.target.value })}
                      placeholder="Quartier Fonds-Saint-Denis"
                      data-testid="input-org-adresse-quartier"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bâtiment / Résidence</Label>
                    <Input
                      value={formData.adresse_batiment}
                      onChange={(e) => setFormData({ ...formData, adresse_batiment: e.target.value })}
                      placeholder="Bât. B"
                      data-testid="input-org-adresse-batiment"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Porte / Appartement / Bureau</Label>
                    <Input
                      value={formData.adresse_porte}
                      onChange={(e) => setFormData({ ...formData, adresse_porte: e.target.value })}
                      placeholder="Porte 12"
                      data-testid="input-org-adresse-porte"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={formData.ville}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={formData.code_postal}
                  onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  maxLength={5}
                />
              </div>
              <div className="space-y-2">
                <Label>Territoire</Label>
                <Select value={formData.territoire} onValueChange={(v) => {
                  const t = findTerritoire(v);
                  setFormData(prev => ({
                    ...prev,
                    territoire: v,
                    telephone_indicatif: (!prev.telephone && t?.dial) ? t.dial : prev.telephone_indicatif,
                  }));
                }}>
                  <SelectTrigger data-testid="select-org-territoire">
                    <SelectValue placeholder="Sélectionner un territoire">
                      {formData.territoire ? (
                        <span className="flex items-center gap-2">
                          <Flag iso={findTerritoire(formData.territoire)?.iso} size={16} />
                          <span>{formData.territoire}</span>
                        </span>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {territoireOptions.map(t => (
                      <SelectItem key={t.value} value={t.value} data-testid={`org-territoire-opt-${t.value}`}>
                        <span className="flex items-center gap-2">
                          <Flag iso={t.iso} size={16} />
                          <span>{t.label}</span>
                          {t.dial ? <span className="text-xs text-[#5E6D66]">{t.dial}</span> : null}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Code NAF/APE officiel (INSEE)</Label>
                <NafAutocomplete
                  value={formData.code_naf ? { code: formData.code_naf, label: formData.naf_label } : null}
                  onChange={(v) => setFormData({ ...formData, code_naf: v?.code || '', naf_label: v?.label || '', activite_principale: v?.label || formData.activite_principale })}
                  testId="org-naf"
                />
              </div>
              <div className="space-y-2">
                <Label>Activité principale (libre)</Label>
                <Input
                  value={formData.activite_principale}
                  onChange={(e) => setFormData({ ...formData, activite_principale: e.target.value })}
                  placeholder="Description détaillée si nécessaire"
                />
              </div>
              <div className="space-y-2">
                <Label>Site web</Label>
                <Input
                  value={formData.site_web}
                  onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                  placeholder="https://"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@entreprise.com"
                  data-testid="input-org-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <PhoneInput
                  indicatif={formData.telephone_indicatif}
                  number={formData.telephone}
                  onChange={({ indicatif, number }) => setFormData({ ...formData, telephone_indicatif: indicatif, telephone: number })}
                  testIdPrefix="org-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effectif</Label>
                <Input
                  type="number"
                  value={formData.effectif}
                  onChange={(e) => setFormData({ ...formData, effectif: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Chiffre d'affaires</Label>
                <CurrencyInput
                  value={formData.chiffre_affaires}
                  onChange={(n) => setFormData({ ...formData, chiffre_affaires: n })}
                  currency="€"
                  testId="input-org-ca"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.statut} onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statutOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="membre_ftpe"
                    checked={formData.membre_ftpe}
                    onCheckedChange={(checked) => setFormData({ ...formData, membre_ftpe: checked })}
                  />
                  <Label htmlFor="membre_ftpe" className="cursor-pointer">Membre FTPE</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-org-btn">
                {editingOrg ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={importDialog} onOpenChange={(v) => { setImportDialog(v); if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importer des organisations depuis Excel</DialogTitle>
            <DialogDescription>Téléchargez le modèle, remplissez-le, puis chargez le fichier. Les doublons SIREN/SIRET sont ignorés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-org-template" className="w-full">
              <DownloadSimple size={16} className="mr-2" /> Télécharger le modèle .xlsx
            </Button>
            <input ref={importFileRef} type="file" accept=".xlsx" className="block w-full text-sm text-[#5E6D66] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#F1EEE8] file:text-[#2D5A4C] hover:file:bg-[#E8E6E1]" data-testid="import-org-file" />
            {importResult && (
              <div className="text-sm rounded border border-[#E8E6E1] p-3 space-y-1" data-testid="import-org-result">
                <p><strong>{importResult.inserted}</strong> organisation(s) créée(s)</p>
                <p><strong>{importResult.skipped}</strong> doublon(s) ignoré(s)</p>
                {importResult.errors?.length > 0 && (
                  <div>
                    <p className="text-red-600 font-semibold mt-1">Erreurs :</p>
                    <ul className="list-disc pl-5 text-xs text-red-700">
                      {importResult.errors.map((e, i) => <li key={i}>Ligne {e.row} : {e.error}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)}>Fermer</Button>
            <Button onClick={handleImportSubmit} disabled={importing} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="submit-org-import">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadSimple size={16} className="mr-2" />}
              Importer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
